import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Printer, Maximize, Minimize, Pencil } from "lucide-react";
import { useLang } from "../i18n";
import { deck } from "./presentation/data";
import Editor from "./presentation/Editor";
import {
  Slide1, Slide2, Slide3, Slide4, Slide5, Slide6, Slide7,
  Slide8, Slide9, Slide10, Slide11, Slide12, Slide13, Slide14,
} from "./presentation/slides";
import "./presentation/presentation.css";

const slideComponents = [Slide1, Slide2, Slide3, Slide4, Slide5, Slide6, Slide7, Slide8, Slide9, Slide10, Slide11, Slide12, Slide13, Slide14];
const TOTAL = slideComponents.length;
const FRAGMENTS = { 4: 3, 5: 6 };
const maxStep = (i) => (FRAGMENTS[i] || 1) - 1;

export default function Presentation() {
  const { lang, setLang } = useLang();
  const [remote, setRemote] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const activeDeck = remote || deck;
  const t = { ...deck[lang], ...(activeDeck[lang] || {}) };
  t.ui = { ...deck[lang].ui, ...(activeDeck[lang]?.ui || {}) };
  const [pos, setPos] = useState({ idx: 0, step: 0 });
  const { idx, step } = pos;
  const rootRef = useRef(null);
  const [isFs, setIsFs] = useState(false);

  const toggleFs = useCallback(() => {
    if (!document.fullscreenElement) {
      rootRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/presentation/content`)
      .then((r) => r.json())
      .then((d) => {
        if (d.content) setRemote(d.content);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onChange = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const go = useCallback((delta) => {
    setPos((p) => {
      if (delta > 0) {
        if (p.step < maxStep(p.idx)) return { idx: p.idx, step: p.step + 1 };
        if (p.idx < TOTAL - 1) return { idx: p.idx + 1, step: 0 };
        return p;
      }
      if (p.step > 0) return { idx: p.idx, step: p.step - 1 };
      if (p.idx > 0) return { idx: p.idx - 1, step: maxStep(p.idx - 1) };
      return p;
    });
  }, []);

  useEffect(() => {
    document.title =
      lang === "pt"
        ? "GT Strategy — Apresentação Institucional"
        : "GT Strategy — Institutional Presentation";
    let robots = document.head.querySelector('meta[name="robots"]');
    const created = !robots;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    const prev = robots.getAttribute("content");
    robots.setAttribute("content", "noindex, nofollow");
    return () => {
      if (created) robots.remove();
      else robots.setAttribute("content", prev || "index, follow");
    };
  }, [lang]);

  useEffect(() => {
    const onKey = (e) => {
      if (editorOpen) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        go(1);
      } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        go(-1);
      } else if (e.key === "Home") {
        setPos({ idx: 0, step: 0 });
      } else if (e.key === "End") {
        setPos({ idx: TOTAL - 1, step: maxStep(TOTAL - 1) });
      } else if (e.key === "f" || e.key === "F") {
        toggleFs();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, toggleFs, editorOpen]);

  const slideData = [t.s1, t.s2, t.s3, t.s4, t.s5, t.s6, t.s7, t.s8, t.s9, t.s10, t.s11, t.s12, t.s13, t.s14];

  return (
    <div ref={rootRef} className={`pres-root flex flex-col ${isFs ? "pres-fs" : ""}`} data-testid="presentation-page">
      <div className="pres-chrome pres-topbar flex items-center justify-between px-6 py-3">
        <Link
          to="/"
          data-testid="pres-back-home"
          className="text-[0.72rem] uppercase tracking-[0.16em] text-[#f1ead9]/60 hover:text-[#f1ead9] transition-colors"
        >
          ← {t.ui.back}
        </Link>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-xs font-medium">
            <button
              data-testid="pres-lang-pt"
              onClick={() => setLang("pt")}
              className={`px-2 py-1 uppercase tracking-widest transition-colors ${lang === "pt" ? "text-[#f1ead9]" : "text-[#f1ead9]/40 hover:text-[#f1ead9]/80"}`}
            >PT</button>
            <span className="text-[#f1ead9]/30">/</span>
            <button
              data-testid="pres-lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 uppercase tracking-widest transition-colors ${lang === "en" ? "text-[#f1ead9]" : "text-[#f1ead9]/40 hover:text-[#f1ead9]/80"}`}
            >EN</button>
          </div>
          <button onClick={() => setEditorOpen(true)} className="pres-chrome-btn" data-testid="pres-edit" title={t.ui.edit}>
            <Pencil size={13} /> {t.ui.edit}
          </button>
          <button onClick={toggleFs} className="pres-chrome-btn" data-testid="pres-fullscreen" title={t.ui.fs}>
            {isFs ? <Minimize size={13} /> : <Maximize size={13} />} {t.ui.fs}
          </button>
          <button onClick={() => window.print()} className="pres-chrome-btn" data-testid="pres-print">
            <Printer size={13} /> {t.ui.print}
          </button>
        </div>
      </div>

      <div className="pres-stage flex-1 flex flex-col justify-center">
        {slideComponents.map((SlideC, i) => (
          <div key={i} className={`pres-slide ${i === idx ? "active" : ""}`} data-testid={i === idx ? "slide-active" : undefined}>
            <SlideC d={slideData[i]} step={i === idx ? step : maxStep(i)} />
          </div>
        ))}
      </div>

      <div className="pres-chrome flex items-center justify-center gap-6 px-6 py-4">
        <button onClick={() => go(-1)} disabled={idx === 0 && step === 0} className="pres-chrome-btn" data-testid="pres-prev" aria-label={t.ui.prev}>
          <ChevronLeft size={15} />
        </button>
        <span className="text-[0.78rem] tracking-[0.3em] text-[#f1ead9]/70 tabular-nums" data-testid="pres-counter">
          {String(idx + 1).padStart(2, "0")} {t.ui.of} {TOTAL}
        </span>
        <button onClick={() => go(1)} disabled={idx === TOTAL - 1 && step === maxStep(TOTAL - 1)} className="pres-chrome-btn" data-testid="pres-next" aria-label={t.ui.next}>
          <ChevronRight size={15} />
        </button>
        {isFs && (
          <button onClick={toggleFs} className="pres-chrome-btn" data-testid="pres-exit-fullscreen" title={t.ui.fs}>
            <Minimize size={13} />
          </button>
        )}
      </div>
      <div className="pres-chrome h-[3px] bg-[#f1ead9]/10">
        <div className="h-full bg-[#6F9C8F] transition-all duration-300" style={{ width: `${((idx + 1) / TOTAL) * 100}%` }} />
      </div>

      <Editor open={editorOpen} onOpenChange={setEditorOpen} content={activeDeck} onSaved={setRemote} />
    </div>
  );
}
